import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  Plus,
  Clock,
  Ban,
  Calendar,
  User,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  UserX,
  Search,
  Filter,
  Building2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { AddBanModal } from '../components/bans/AddBanModal';
import { AddSuspensionModal } from '../components/bans/AddSuspensionModal';
import { useBansStore } from '../stores/bansStore';
import { useSuspensionStore } from '../stores/suspensionStore';
import { useGuidesStore } from '../stores/guidesStore';
import { useOrganizersStore } from '../stores/organizersStore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { collection, query, where, getDocs, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import toast from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';

type DisputeBanGroup = {
  organizerId: string;
  organizerName: string;
  guides: Array<{
    id: string;
    name: string;
    pilgrimId: string;
    pilgrimName: string;
    disputeId: string;
    createdAt: number;
  }>;
};

export const BansManagementPage = () => {
  const { translations } = useLanguage();
  const [isAddBanModalOpen, setIsAddBanModalOpen] = useState(false);
  const [isAddSuspensionModalOpen, setIsAddSuspensionModalOpen] = useState(false);
  const [disputeBans, setDisputeBans] = useState<DisputeBanGroup[]>([]);
  const [loadingDisputeBans, setLoadingDisputeBans] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedOrganizer, setSelectedOrganizer] = useState('');
  const [expandedOrganizer, setExpandedOrganizer] = useState<string | null>(null);
  const [expandedBan, setExpandedBan] = useState<string | null>(null);
  const [expandedSuspension, setExpandedSuspension] = useState<string | null>(null);
  const [disputeBanStats, setDisputeBanStats] = useState({
    totalBans: 0,
    totalOrganizers: 0,
    totalGuides: 0,
    recentBans: 0, // Last 7 days
  });
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [banToRemove, setBanToRemove] = useState<string | null>(null);
  const [suspensionToRemove, setSuspensionToRemove] = useState<string | null>(null);
  const [disputeBanToRemove, setDisputeBanToRemove] = useState<string | null>(null);

  const {
    bans,
    loading: bansLoading,
    error: bansError,
    initializeListener: initializeBans,
    removeBan
  } = useBansStore();

  const {
    suspensions,
    loading: suspensionsLoading,
    error: suspensionsError,
    initializeListener: initializeSuspensions,
    removeSuspension
  } = useSuspensionStore();

  const { guides, initializeListener: initializeGuides } = useGuidesStore();
  const { organizers, fetchOrganizers, loading: organizersLoading } = useOrganizersStore();

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      initializeBans();
      initializeSuspensions();
      initializeGuides();
      await fetchOrganizers();
      fetchDisputeBans();
    };

    initializeData();
  }, [initializeBans, initializeSuspensions, initializeGuides, fetchOrganizers]);

  const fetchDisputeBans = async () => {
    try {
      setLoadingDisputeBans(true);
      console.log('Fetching dispute bans...');

      // Get all guide bans
      const bansRef = collection(firestore, 'GuideBans');
      const bansSnapshot = await getDocs(bansRef);

      // Create a map to store unique pilgrim IDs
      const pilgrimIds = new Set<string>();
      bansSnapshot.docs.forEach(doc => {
        pilgrimIds.add(doc.data().pilgrimId);
      });

      // Batch pilgrim queries
      const pilgrimData = new Map();
      const batchSize = 30;
      const pilgrimIdArray = Array.from(pilgrimIds);
      
      for (let i = 0; i < pilgrimIdArray.length; i += batchSize) {
        const batch = pilgrimIdArray.slice(i, i + batchSize);
        const pilgrimsRef = collection(firestore, 'Pilgrims');
        const q = query(pilgrimsRef, where('__name__', 'in', batch));
        const pilgrimsSnapshot = await getDocs(q);
        
        pilgrimsSnapshot.docs.forEach(doc => {
          pilgrimData.set(doc.id, doc.data());
        });
      }

      // Group bans by organizer
      const bansByOrganizer = new Map<string, DisputeBanGroup>();

      for (const doc of bansSnapshot.docs) {
        const banData = doc.data();
        const guideData = guides[banData.guideId];
        const organizerId = guideData?.organizerId;
        const organizerData = organizers[organizerId];
        
        if (!guideData || !organizerData) continue;

        const pilgrimInfo = pilgrimData.get(banData.pilgrimId);
        if (!pilgrimInfo) continue;

        if (!bansByOrganizer.has(organizerId)) {
          bansByOrganizer.set(organizerId, {
            organizerId,
            organizerName: organizerData.name,
            guides: []
          });
        }

        const group = bansByOrganizer.get(organizerId)!;
        group.guides.push({
          id: doc.id, // Changed to use document ID instead of guideId
          name: guideData.name,
          pilgrimId: banData.pilgrimId,
          pilgrimName: pilgrimInfo.Name,
          disputeId: banData.disputeId,
          createdAt: banData.createdAt
        });
      }

      // Convert map to array and sort
      const groupedBans = Array.from(bansByOrganizer.values())
        .sort((a, b) => b.guides.length - a.guides.length);

      // Calculate stats
      const now = Date.now();
      const sevenDaysAgo = now - (7 * 24 * 60 * 60 * 1000);
      
      const stats = {
        totalBans: bansSnapshot.size,
        totalOrganizers: bansByOrganizer.size,
        totalGuides: new Set(bansSnapshot.docs.map(doc => doc.data().guideId)).size,
        recentBans: bansSnapshot.docs.filter(doc => doc.data().createdAt > sevenDaysAgo).length
      };

      setDisputeBanStats(stats);
      setDisputeBans(groupedBans);
      console.log('Dispute bans fetched:', { 
        totalGroups: groupedBans.length,
        totalBans: stats.totalBans
      });
    } catch (error) {
      console.error('Error fetching dispute bans:', error);
      toast.error(translations?.bansManagement?.errors?.fetchDisputeBans || 'Failed to fetch dispute bans');
    } finally {
      setLoadingDisputeBans(false);
    }
  };

  const handleRemoveDisputeBan = async () => {
    if (!disputeBanToRemove) return;
    
    try {
      // Delete the document directly using the document ID
      await deleteDoc(doc(firestore, 'GuideBans', disputeBanToRemove));
      
      // Refresh data
      await fetchDisputeBans();
      toast.success(translations?.bansManagement?.success?.removeDisputeBan || 'Ban removed successfully');
    } catch (error) {
      console.error('Error removing dispute ban:', error);
      toast.error(translations?.bansManagement?.errors?.removeDisputeBan || 'Failed to remove ban');
    } finally {
      setDisputeBanToRemove(null);
      setIsConfirmDialogOpen(false);
    }
  };

  const confirmRemoveDisputeBan = (banId: string) => {
    setDisputeBanToRemove(banId);
    setIsConfirmDialogOpen(true);
  };

  const handleRemoveBan = async () => {
    if (!banToRemove) return;
    
    try {
      await removeBan(banToRemove);
      toast.success(translations?.bansManagement?.success?.removeBan || 'Ban removed successfully');
    } catch (error) {
      console.error('Error removing ban:', error);
      toast.error(translations?.bansManagement?.errors?.removeBan || 'Failed to remove ban');
    } finally {
      setBanToRemove(null);
      setIsConfirmDialogOpen(false);
    }
  };

  const handleRemoveSuspension = async () => {
    if (!suspensionToRemove) return;
    
    try {
      await removeSuspension(suspensionToRemove);
      toast.success(translations?.bansManagement?.success?.removeSuspension || 'Suspension removed successfully');
    } catch (error) {
      console.error('Error removing suspension:', error);
      toast.error(translations?.bansManagement?.errors?.removeSuspension || 'Failed to remove suspension');
    } finally {
      setSuspensionToRemove(null);
      setIsConfirmDialogOpen(false);
    }
  };

  // Filter dispute bans
  const filteredDisputeBans = disputeBans
    .map(group => ({
      ...group,
      guides: group.guides.filter(guide => {
        const matchesSearch = !searchTerm || 
          guide.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          guide.pilgrimName.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesDateRange = (!dateRange.start || !dateRange.end) || 
          (guide.createdAt >= new Date(dateRange.start).getTime() &&
           guide.createdAt <= new Date(dateRange.end).getTime());

        return matchesSearch && matchesDateRange;
      })
    }))
    .filter(group => 
      (group.guides.length > 0) && 
      (!selectedOrganizer || group.organizerId === selectedOrganizer)
    );

  // Filter regular bans
  const filteredBans = Object.values(bans).filter(ban => {
    const matchesSearch = !searchTerm || 
      ban.userName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDateRange = (!dateRange.start || !dateRange.end) || 
      (ban.createdAt >= new Date(dateRange.start).getTime() &&
       ban.createdAt <= new Date(dateRange.end).getTime());

    return matchesSearch && matchesDateRange;
  });

  // Filter suspensions
  const filteredSuspensions = Object.values(suspensions).filter(suspension => {
    const matchesSearch = !searchTerm || 
      suspension.reason.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDateRange = (!dateRange.start || !dateRange.end) || 
      (suspension.createdAt >= new Date(dateRange.start).getTime() &&
       suspension.createdAt <= new Date(dateRange.end).getTime());

    return matchesSearch && matchesDateRange;
  });

  // Calculate ban stats
  const banStats = {
    total: filteredBans.length,
    active: filteredBans.filter(ban => ban.isActive).length,
    expired: filteredBans.filter(ban => 
      ban.expiresAt !== null && ban.expiresAt < Date.now()
    ).length,
    permanent: filteredBans.filter(ban => ban.expiresAt === null).length
  };

  // Calculate suspension stats
  const suspensionStats = {
    total: filteredSuspensions.length,
    active: filteredSuspensions.filter(suspension => suspension.isActive).length,
    linking: filteredSuspensions.filter(suspension => suspension.type === 'linking').length,
    accommodation: filteredSuspensions.filter(suspension => 
      suspension.type === 'all_accommodation' || suspension.type === 'specific_hotel'
    ).length,
    camp: filteredSuspensions.filter(suspension => suspension.type === 'camp_accommodation').length
  };

  if (bansLoading || suspensionsLoading || loadingDisputeBans || organizersLoading) {
    return <LoadingSpinner />;
  }

  if (bansError || suspensionsError) {
    return <ErrorMessage message={bansError || suspensionsError} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-50 rounded-lg">
            <Shield className="text-red-500" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {translations?.bansManagement?.header?.title || 'Bans and Suspensions Management'}
            </h1>
            <p className="text-gray-500 mt-1">
              {translations?.bansManagement?.header?.subtitle || 'Manage user bans and service suspensions'}
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setIsAddBanModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            <Plus size={20} />
            <span>{translations?.bansManagement?.buttons?.addBan || 'Add New Ban'}</span>
          </button>
          <button
            onClick={() => setIsAddSuspensionModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
          >
            <Plus size={20} />
            <span>{translations?.bansManagement?.buttons?.addSuspension || 'Suspend Service'}</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder={translations?.bansManagement?.filters?.searchPlaceholder || "Search by guide or pilgrim name..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          </div>

          {/* Date Range */}
          <div className="flex gap-2">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Organizer Filter */}
          <select
            value={selectedOrganizer}
            onChange={(e) => setSelectedOrganizer(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Organizers</option>
            {Object.values(organizers).map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        </div>

        {/* Active Filters */}
        {(searchTerm || dateRange.start || dateRange.end || selectedOrganizer) && (
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-primary-500" />
            <span className="text-sm text-gray-600">Active Filters:</span>
            <div className="flex flex-wrap gap-2">
              {searchTerm && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  Search: {searchTerm}
                </span>
              )}
              {(dateRange.start || dateRange.end) && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  Date: {dateRange.start || 'Start'} - {dateRange.end || 'End'}
                </span>
              )}
              {selectedOrganizer && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  Organizer: {organizers[selectedOrganizer]?.name}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Dispute Bans Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-red-50 rounded-lg">
              <Ban className="text-red-500" size={20} />
            </div>
            <h3 className="font-medium">
              {translations?.bansManagement?.stats?.disputeBans || 'Dispute Bans'}
            </h3>
          </div>
          <p className="text-2xl font-bold text-red-600">{disputeBanStats.totalBans}</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{disputeBanStats.totalOrganizers}</span> organizers
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">{disputeBanStats.totalGuides}</span> guides
            </div>
          </div>
        </motion.div>

        {/* Regular Bans Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <UserX className="text-yellow-500" size={20} />
            </div>
            <h3 className="font-medium">
              {translations?.bansManagement?.stats?.regularBans || 'Regular Bans'}
            </h3>
          </div>
          <p className="text-2xl font-bold text-yellow-600">{banStats.total}</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{banStats.active}</span> active
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">{banStats.permanent}</span> permanent
            </div>
          </div>
        </motion.div>

        {/* Suspensions Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-50 rounded-lg">
              <AlertTriangle className="text-blue-500" size={20} />
            </div>
            <h3 className="font-medium">
              {translations?.bansManagement?.stats?.suspensions || 'Service Suspensions'}
            </h3>
          </div>
          <p className="text-2xl font-bold text-blue-600">{suspensionStats.total}</p>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{suspensionStats.linking}</span> linking
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">{suspensionStats.accommodation}</span> accom.
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">{suspensionStats.camp}</span> camp
            </div>
          </div>
        </motion.div>
      </div>

      {/* Regular Bans Section */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">
          {translations?.bansManagement?.regularBans?.title || 'Regular Bans'}
        </h2>
        {filteredBans.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ban Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expiry
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBans.map(ban => (
                    <tr key={ban.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-primary-50 rounded-full">
                            <User className="h-5 w-5 text-primary-500" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{ban.userName}</div>
                            <div className="text-sm text-gray-500">{ban.userType}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {ban.userType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          {ban.banType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {ban.expiresAt ? new Date(ban.expiresAt).toLocaleDateString() : 'Permanent'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          ban.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {ban.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button 
                          onClick={() => {
                            setBanToRemove(ban.id);
                            setIsConfirmDialogOpen(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                        <button 
                          onClick={() => setExpandedBan(expandedBan === ban.id ? null : ban.id)}
                          className="ml-4 text-primary-600 hover:text-primary-900"
                        >
                          {expandedBan === ban.id ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Expanded Ban Details */}
            {expandedBan && (
              <div className="p-4 bg-gray-50 border-t">
                {Object.values(bans).filter(ban => ban.id === expandedBan).map(ban => (
                  <div key={ban.id} className="space-y-2">
                    <h3 className="font-medium">Ban Details</h3>
                    <p><span className="font-medium">Reason:</span> {ban.reason}</p>
                    <p><span className="font-medium">Created by:</span> {ban.createdByName}</p>
                    <p><span className="font-medium">Created at:</span> {new Date(ban.createdAt).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg">
            <p className="text-gray-500">
              {translations?.bansManagement?.regularBans?.noBans || 'No regular bans found'}
            </p>
          </div>
        )}
      </div>

      {/* Suspensions Section */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">
          {translations?.bansManagement?.suspensions?.title || 'Service Suspensions'}
        </h2>
        {filteredSuspensions.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Target
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expiry
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSuspensions.map(suspension => (
                    <tr key={suspension.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          {suspension.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {suspension.type === 'specific_hotel' ? (
                          <span className="text-sm">{suspension.hotelName || 'Unknown Hotel'}</span>
                        ) : (
                          <span className="text-sm">All System</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {suspension.expiresAt ? new Date(suspension.expiresAt).toLocaleDateString() : 'Permanent'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          suspension.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {suspension.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button 
                          onClick={() => {
                            setSuspensionToRemove(suspension.id);
                            setIsConfirmDialogOpen(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                        <button 
                          onClick={() => setExpandedSuspension(expandedSuspension === suspension.id ? null : suspension.id)}
                          className="ml-4 text-primary-600 hover:text-primary-900"
                        >
                          {expandedSuspension === suspension.id ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Expanded Suspension Details */}
            {expandedSuspension && (
              <div className="p-4 bg-gray-50 border-t">
                {Object.values(suspensions).filter(suspension => suspension.id === expandedSuspension).map(suspension => (
                  <div key={suspension.id} className="space-y-2">
                    <h3 className="font-medium">Suspension Details</h3>
                    <p><span className="font-medium">Reason:</span> {suspension.reason}</p>
                    <p><span className="font-medium">Created by:</span> {suspension.createdByName}</p>
                    <p><span className="font-medium">Created at:</span> {new Date(suspension.createdAt).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg">
            <p className="text-gray-500">
              {translations?.bansManagement?.suspensions?.noSuspensions || 'No service suspensions found'}
            </p>
          </div>
        )}
      </div>

      {/* Dispute Bans Section */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">
          {translations?.bansManagement?.disputeBans?.title || 'Dispute Bans'}
        </h2>
        {filteredDisputeBans.map((group) => (
          <motion.div
            key={group.organizerId}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm overflow-hidden"
          >
            <div 
              className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setExpandedOrganizer(
                expandedOrganizer === group.organizerId ? null : group.organizerId
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-50 rounded-full">
                    <Building2 className="text-red-500" size={20} />
                  </div>
                  <div>
                    <h3 className="font-medium">{group.organizerName}</h3>
                    <p className="text-sm text-gray-500">
                      {group.guides.length} banned guides
                    </p>
                  </div>
                </div>
                {expandedOrganizer === group.organizerId ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </div>
            </div>

            <AnimatePresence>
              {expandedOrganizer === group.organizerId && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="border-t"
                >
                  <div className="divide-y">
                    {group.guides.map((guide) => (
                      <div key={`${guide.id}-${guide.pilgrimId}`} className="p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-red-50 rounded-full">
                              <User className="text-red-500" size={20} />
                            </div>
                            <div>
                              <div className="font-medium">{guide.name}</div>
                              <div className="text-sm text-gray-500">
                                Banned from pilgrim: {guide.pilgrimName}
                              </div>
                              <div className="text-sm text-gray-500 mt-1">
                                <Calendar size={14} className="inline mr-1" />
                                {new Date(guide.createdAt).toLocaleDateString('en-US')}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent expanding/collapsing
                              confirmRemoveDisputeBan(guide.id);
                            }}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <XCircle size={20} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}

        {filteredDisputeBans.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg">
            <p className="text-gray-500">
              {translations?.bansManagement?.disputeBans?.noBans || 'No dispute bans found'}
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddBanModal
        isOpen={isAddBanModalOpen}
        onClose={() => setIsAddBanModalOpen(false)}
        guides={guides}
        organizers={organizers}
      />

      <AddSuspensionModal
        isOpen={isAddSuspensionModalOpen}
        onClose={() => setIsAddSuspensionModalOpen(false)}
      />

      {/* Confirm Dialog for Ban Removal */}
      <ConfirmDialog
        isOpen={isConfirmDialogOpen && !!banToRemove}
        onClose={() => {
          setIsConfirmDialogOpen(false);
          setBanToRemove(null);
        }}
        onConfirm={handleRemoveBan}
        title={translations?.bansManagement?.confirmDialog?.removeBanTitle || "Remove Ban"}
        message={translations?.bansManagement?.confirmDialog?.removeBanMessage || "Are you sure you want to remove this ban? This action cannot be undone."}
        type="danger"
      />

      {/* Confirm Dialog for Suspension Removal */}
      <ConfirmDialog
        isOpen={isConfirmDialogOpen && !!suspensionToRemove}
        onClose={() => {
          setIsConfirmDialogOpen(false);
          setSuspensionToRemove(null);
        }}
        onConfirm={handleRemoveSuspension}
        title={translations?.bansManagement?.confirmDialog?.removeSuspensionTitle || "Remove Suspension"}
        message={translations?.bansManagement?.confirmDialog?.removeSuspensionMessage || "Are you sure you want to remove this service suspension? This action cannot be undone."}
        type="danger"
      />
      
      {/* Confirm Dialog for Dispute Ban Removal */}
      <ConfirmDialog
        isOpen={isConfirmDialogOpen && !!disputeBanToRemove}
        onClose={() => {
          setIsConfirmDialogOpen(false);
          setDisputeBanToRemove(null);
        }}
        onConfirm={handleRemoveDisputeBan}
        title={translations?.bansManagement?.confirmDialog?.removeDisputeBanTitle || "Remove Dispute Ban"}
        message={translations?.bansManagement?.confirmDialog?.removeDisputeBanMessage || "Are you sure you want to remove this dispute ban? This action cannot be undone."}
        type="danger"
      />
    </div>
  );
};